import { KeyboardEvent, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Input } from 'us-react-components/dist/Input/Input';
import { onEnterAction } from 'us-react-components/dist/utils/a11y';
import { axios } from 'us-web-services';
import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { ActiveCredits } from './components/ActiveCredits';
import { InactiveCredits } from './components/InactiveCredits';

export const Credits = observer((): JSX.Element => {
  document.title = 'UrbanSitter - Credits';
  const { stores } = useDependencies();
  const { t } = useTranslation();
  const [voucher, setVoucher] = useState<string>('');

  const handleClick = async (): Promise<void> => {
    const voucherCode = voucher.trim();

    if (!stores.user.user || !voucherCode) return;

    try {
      const redeemResponse = await stores.credits.redeemVoucher(voucherCode);

      if (redeemResponse.errors) {
        toast.error(redeemResponse.errors[0].message);
      } else {
        toast.success(t('notifications.credits.success'), { icon: '🎉' });
        setVoucher('');
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data.errors) {
        toast.error(e.response.data.errors[0].message);
      } else {
        console.warn(e);
      }
    }
  };

  return (
    <div className='container py-6'>
      <h1 className='text-2xl font-semibold'>{t('pages.credits.header')}</h1>
      <p>{t('pages.credits.enterVoucherToRedeem')}</p>
      <div className='flex items-center mb-10'>
        <Input
          id='voucher-code'
          className='flex-1 mr-6'
          label={t('pages.credits.voucherCode')}
          type='text'
          onChange={(e): void => setVoucher(e.target.value)}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>): void =>
            onEnterAction(e, async () => {
              if (!voucher.trim()) return;
              await handleClick();
            })
          }
          value={voucher}
        />
        <div className='flex-1'>
          <Button buttonType={ButtonType.primary} disabled={!voucher} onClick={handleClick}>
            {t('pages.credits.redeem')}
          </Button>
        </div>
      </div>
      {stores.credits.loadError && (
        <p className='text-red-600'>{t('pages.credits.loadCreditsError')}</p>
      )}
      <ActiveCredits />
      <InactiveCredits />
    </div>
  );
});
